<template>
    <div>
        <v-dialog :persistent="persistent" :value="value" max-width="700px" @keydown.esc.prevent="cancel">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ $t('create_location') }}</span>
                </v-card-title>

                <v-card-text>
                    <v-form ref="form" v-model="valid">
                        <v-container>
                            <!-- bezeichnung -->
                            <v-row dense>
                                <v-col cols="12">
                                    <LabelField :label="$t('bezeichnung')">
                                        <v-text-field
                                            v-model="title"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :counter="60"
                                            :rules="[getRule('required'), getRule('maxLength')(60)]"
                                        />
                                    </LabelField>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <!-- plz -->
                                <v-col cols="12" lg="4">
                                    <LabelField :label="$t('plz')">
                                        <v-text-field
                                            v-model="zip"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :rules="[getRule('required'), getRule('maxLength')(10)]"
                                        />
                                    </LabelField>
                                </v-col>
                                <!-- ort -->
                                <v-col cols="12" lg="8">
                                    <LabelField :label="$t('ort')">
                                        <v-text-field
                                            v-model="city"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            :rules="[getRule('required'), getRule('maxLength')(100)]"
                                        />
                                    </LabelField>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <slot name="actions">
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
                        <v-btn :disabled="!valid || loading" color="action" text @click="confirm">{{
                            $t('create')
                        }}</v-btn>
                    </slot>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <ConfirmDialog
            v-if="showConfirmDialog"
            :value="showConfirmDialog"
            :title="$t('create_location')"
            :message="confirmMsg"
            :confirm-label="$t('edit_data')"
            @input="showConfirmDialog = $event"
            @cancel="cancel"
        />
    </div>
</template>

<script>
import getRulesMixin from '@/mixins/get-form-rules';
import { Location, LocationCollection } from '@/models/Location';
import { Address } from '@/models/Address';
import { userIsStaff } from '@/helpers/auth';
import { mapActions } from 'vuex';
import { userInfo } from '@/helpers/globals';

export default {
    name: 'AddressNewRecordDialog',
    mixins: [getRulesMixin],

    props: {
        persistent: {
            type: Boolean,
            default: true,
        },
        value: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        const locationCollection = new LocationCollection();
        if (!userIsStaff() && this.showAllLocations) {
            locationCollection.addExtraParam('showAllLocations', true);
        }
        return {
            collection: locationCollection,
            valid: null,
            title: null,
            zip: null,
            city: null,
            showConfirmDialog: false,
            loadingCount: 0,
        };
    },

    computed: {
        loading() {
            return this.loadingCount > 0;
        },
        confirmMsg() {
            let msg = [this.$t('address_already_exists'), '', this.$t('existing_addresses')];
            this.collection.each((addr) => {
                msg.push(`- ${addr.titleWithLocation}`);
            });
            return msg.join('<br>');
        },
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        ...mapActions(['runAsync']),

        validateForm() {
            this.$refs.form.validate();
        },

        cancel() {
            this.$emit('cancel');
            this.$emit('input', false);
        },

        async confirm() {
            this.loadingCount++;
            try {
                this.collection.setFilter('title', this.title);
                this.collection.setFilter('zip', this.zip);
                this.collection.setFilter('city', this.city);
                await this.runAsync(async () => this.collection.query());
                if (this.collection.length > 0) {
                    this.showConfirmDialog = true;
                } else {
                    await this.saveNewAddress();
                }
            } finally {
                this.loadingCount--;
            }
        },

        async saveNewAddress() {
            this.loadingCount++;
            try {
                const type = 'location';
                const legal_type = 'organization';
                const address = new Address({
                    type,
                    legal_type,
                    title: this.title,
                    zip: this.zip,
                    city: this.city,
                });
                const location = new Location({
                    event_organizer_id: userIsStaff() ? null : userInfo().event_organizer_id,
                    legal_type,
                });

                await this.runAsync(() => address.save());
                location.address_id = address.getId();
                await this.runAsync(() => location.save());

                this.$emit('input', false);
                this.$emit('new-address', address, location);
            } finally {
                this.loadingCount--;
            }
        },
    },
};
</script>
